import moment from "moment";
import { generateClient } from "aws-amplify/api";
import apolloClient from "@/clientApollo";
import { gql } from "@apollo/client";
const client = generateClient();

export class Subscription {
	constructor(...params) {
		const [key, index, queryClient, persistent = false] = params;
		this.key = key || null;
		this.index = index || null;
		this.timestamp = moment();
		this.subscriptions = null;
		this.persistent = Boolean(persistent);
		this.enabled = false;
		this.invalidate = null;
		this.invalidateList = null;
		this.invalidateType = "simple";
		this.queryClient = queryClient || null;
	}

	getKey() {
		return this.key;
	}

	getIndex() {
		return this.index;
	}

	getLastKeyIdentifier() {
		let lastKeyIdentifier = null;
		if (this.key?.length) {
			lastKeyIdentifier = [...this.key].pop();
		}
		return lastKeyIdentifier;
	}

	getTimeStamp() {
		return this.timestamp;
	}

	getSize() {
		return this.subscriptions ? Object.keys(this.subscriptions)?.length : 0;
	}

	getPersistentSize() {
		return this.persistent ? this.getSize() : 0;
	}

	isPersistent() {
		return this.persistent;
	}

	getSubscriptions() {
		return this.subscriptions;
	}

	getInvalidateIndexes() {
		if (this.invalidateType === "list") {
			return this.invalidateList;
		}

		return this.invalidate;
	}

	getInvalidateType() {
		return this.invalidateType;
	}

	getQueryClient() {
		return this.queryClient;
	}

	addSubscriptionsList(subscriptionsList = []) {
		if (subscriptionsList?.length) {
			subscriptionsList.forEach((subscription) => {
				this._addSubscription(subscription);
			});
		}
	}

	setPersistent(value) {
		this.persistent = Boolean(value);
	}

	setInvalidate(invalidate = []) {
		if (invalidate?.length) {
			this.invalidateType = "simple";
			this.invalidateList = null;
			this.invalidate = invalidate;
		}
	}

	setInvalidateList(invalidate = []) {
		if (invalidate?.length) {
			this.invalidateType = "list";
			this.invalidate = null;
			this.invalidateList = invalidate;
		}
	}

	removeSubscriptions() {
		this.subscriptions = null;
	}

	clean() {
		if (this.subscriptions) {
			for (const subscription of Object.values(this.subscriptions)) {
				subscription.clean();
			}
		}

		if (this.queryClient) {
			this._invalidateCache();
		}

		this.subscriptions = null;
		this.persistent = false;
		this.enabled = false;
		this.invalidate = null;
		this.invalidateList = null;
		this.invalidateType = "simple";
		this.queryClient = null;
	}

	_invalidateCache() {
		if (this.invalidate || this.invalidateList) {
			if (this.invalidateType === "list" && this.invalidateList?.length) {
				this.invalidateList.forEach((cacheKey) => {
					this.queryClient.resetQueries(cacheKey);
				});
			} else {
				this.queryClient.resetQueries(this.invalidate);
			}
		}
	}

	_addSubscription(subscription) {
		if (!this.subscriptions) {
			this.subscriptions = {};
		}
		this.subscriptions[subscription.context] = new SubscriptionDefinition(subscription);
	}
}

class SubscriptionDefinition {
	constructor({ context, query, variables }) {
		this.context = context || null;
		this.subscription = null;
		this.query = query || null;
		this.variables = variables || null;
		this.callback = null;
	}

	getVariables() {
		return this.variables;
	}

	getCallback() {
		return this.callback;
	}

	setCallback(newCallback) {
		this.callback = newCallback;
		if (!this.subscription) {
			if (import.meta.env.VITE_APP_AWS_APPSYNC_RTC === "http://localhost:4000/") {
				const queryAux = gql`
					${this.query}
				`;

				const susAux = apolloClient
					.subscribe({
						query: queryAux,
						variables: this.variables,
					})
					.subscribe({
						next: (value) => {
							if (this.callback) {
								this.callback(value);
							}
						},
						error: () => {
							console.error(this.context);
						},
					});
				this.subscription = susAux;
			} else {
				this.subscription = client.graphql({ query: this.query, variables: this.variables }).subscribe({
					next: (value) => {
						if (this.callback) {
							this.callback(value);
						}
					},
				});
			}
		}

		return this.callback;
	}

	clean() {
		this.context = null;
		this.query = null;
		this.variables = null;
		this.callback = null;

		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}
